import { usePortal, type AuditRow, type ClientDetail } from "@app/portal-sdk";
import { Alert, DataTable, EmptyState, Stack, Typography } from "@app/ui-kit";
import { AuditChanges } from "../../components/AuditChanges";
import { LoadingScreen } from "../../components/LoadingScreen";
import { displayName, formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/useApiQuery";

type Props = { client: ClientDetail };

const ACTION_LABELS: Record<string, string> = {
  "client.create": "Created client",
  "client.update": "Updated client",
  "client.suspend": "Suspended client",
  "client.reactivate": "Reactivated client",
  "branding.update": "Updated branding",
  "logo.update": "Updated logo",
  "logo.remove": "Removed logo",
  "member.invite": "Invited member",
  "member.resend": "Resent invitation",
  "member.update": "Updated member",
  "member.activate": "Accepted invitation",
  "member.jit_provision": "Provisioned via SSO",
  "app.update": "Changed application access",
  "file.upload": "Uploaded file",
  "file.download": "Downloaded file",
  "file.delete": "Deleted file",
  "admin.enter_client": "BKDP opened the portal"
};

export const ActivityTab = ({ client }: Props) => {
  const { api } = usePortal();
  const rows = useApiQuery<AuditRow[]>(
    () => api.request<AuditRow[]>(`/admin/clients/${client.id}/audit?limit=100`),
    [api, client.id]
  );

  if (rows.loading && !rows.data) return <LoadingScreen minHeight={200} />;
  if (rows.error) return <Alert severity="error">{rows.error}</Alert>;
  if ((rows.data ?? []).length === 0) {
    return <EmptyState eyebrow="Activity" title="Nothing recorded yet" />;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Admin actions, invitations, and file uploads and downloads for this client.
      </Typography>
      <DataTable
        columns={[
          { key: "when", label: "When" },
          { key: "who", label: "Who" },
          { key: "action", label: "Action" },
          { key: "target", label: "Target" },
          { key: "changes", label: "What changed" }
        ]}
        rows={(rows.data ?? []).map((row) => ({
          when: formatDateTime(row.createdAt),
          who: displayName(row.actor),
          action: ACTION_LABELS[row.action] ?? row.action,
          target: row.target ?? "—",
          changes: <AuditChanges row={row} />
        }))}
        maxHeight={560}
        stickyHeader
      />
    </Stack>
  );
};
